import { useCallback, useEffect, useRef } from 'react';

import { toFailureReason } from '../failureReason';
import {
    type ImageGenerationSubmitAction,
    type ImageGenerationSubmitState,
    type ImageGenerationPollResponse,
} from '../types';
import { type PollResult, useGenerationPolling } from './useGenerationPolling';

export type UseImageGenerationPollingProps = {
    imageGenerationSubmitState: ImageGenerationSubmitState;
    dispatchImageGenerationSubmit: (action: ImageGenerationSubmitAction) => void;
    refreshCredits: () => Promise<number>;
};

export const useImageGenerationPolling = ({
    imageGenerationSubmitState,
    dispatchImageGenerationSubmit,
    refreshCredits,
}: UseImageGenerationPollingProps): void => {
    const imageSubmitStateRef = useRef<ImageGenerationSubmitState>(imageGenerationSubmitState);
    useEffect(() => {
        imageSubmitStateRef.current = imageGenerationSubmitState;
    });

    const handleImageGenerationPoll = useCallback(
        async (taskId: number): Promise<PollResult> => {
            const res = await fetch(`/api/image-generation/${taskId}`);
            const payload = (await res.json()) as ImageGenerationPollResponse;

            if (payload.status === 'SUCCEEDED' && payload.imageUrl) {
                const { aiContentId, lastAiContentId, aiContentType, aiModelName } = imageSubmitStateRef.current;
                dispatchImageGenerationSubmit({
                    type: 'GENERATION_SUCCESS',
                    imageUrl: payload.imageUrl,
                    aiContentId,
                    lastAiContentId,
                    aiContentType,
                    aiModelName,
                });
                return 'success';
            }

            if (payload.status === 'FAILED' || payload.status === 'CANCELLED') {
                void refreshCredits();
                dispatchImageGenerationSubmit({
                    type: 'GENERATION_ERROR',
                    failureReason: toFailureReason(payload.failureReason) ?? 'generation_failed',
                });
                return 'error';
            }

            return 'continue';
        },
        [dispatchImageGenerationSubmit, refreshCredits],
    );

    useGenerationPolling({
        taskId: imageGenerationSubmitState.pendingTaskId,
        status: imageGenerationSubmitState.status,
        onPoll: handleImageGenerationPoll,
        onTimeout: () => {
            void refreshCredits();
            dispatchImageGenerationSubmit({ type: 'GENERATION_ERROR' });
        },
    });
};
